/**
 * Iter 123 — Iframe-safe external redirect.
 * Iter 126 — Hardened for sandboxed iframes.
 *
 * Physiogenic often runs inside an iframe (preview URLs, ISV embeds). When
 * we want to send the browser to a third-party site that blocks framing
 * (Stripe checkout / customer portal, Google OAuth consent, magic-link
 * handlers), a plain `window.location.href = url` navigates the *iframe*,
 * which the third party then refuses to render → "refused to connect".
 *
 * Strategies, tried in order:
 *   1. `window.top.location.href = url` — cleanest breakout. Only works
 *      when the parent frame is same-origin (SecurityError otherwise).
 *   2. Programmatic click on `<a target="_blank">` — user-agent-native
 *      navigation initiated from within the original click gesture. This
 *      survives popup blockers in sandboxed iframes far better than
 *      `window.open`, because the browser treats the anchor click as a
 *      user action, not a scripted popup.
 *   3. `window.open(url, '_blank', 'noreferrer')` — last-chance new tab.
 *   4. If nothing worked, return `false` so the caller can render a manual
 *      "Click here to open" fallback (never silently navigate the iframe
 *      into a URL that Stripe/Google will refuse to frame).
 *
 * Returns `true` if a redirect was initiated, `false` if the URL couldn't
 * be opened (caller should show the URL as a clickable link).
 */
export function redirectExternal(url) {
  if (!url) return false;

  // Strategy 1 — same-origin parent frame: replace the top window.
  try {
    const top = window.top;
    if (top && top !== window) {
      // Reading `top.location.href` throws SecurityError when cross-origin;
      // the *write* would also throw, so probe first to fail fast.
      // eslint-disable-next-line no-unused-vars
      const _probe = top.location.href;
      top.location.href = url;
      return true;
    }
    if (top === window) {
      // Not in an iframe at all — plain navigation is fine.
      window.location.href = url;
      return true;
    }
  } catch (_) {
    /* Cross-origin `window.top` — fall through to the anchor strategy. */
  }

  // Strategy 2 — anchor click. Most reliable inside sandboxed iframes as
  // long as we're still within the synchronous portion of a user click.
  try {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    // Some browsers require the element to be in the DOM before .click()
    // will fire (Safari in particular).
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    // Detach shortly after so the browser has time to process the click.
    setTimeout(() => { try { a.remove(); } catch (_) {} }, 200);
    return true;
  } catch (_) { /* fall through */ }

  // Strategy 3 — window.open as last-chance popup.
  try {
    const w = window.open(url, '_blank', 'noreferrer');
    if (w) return true;
  } catch (_) { /* fall through */ }

  // Strategy 4 — refuse to navigate the iframe (Stripe will just refuse
  // to render). Let the caller present the URL as a copy-able link.
  return false;
}
